/* eslint-disable react-hooks/exhaustive-deps */
import * as React from 'react'
import { Outlet } from 'react-router-dom'

import { useQuery } from '@apollo/client/react'
import { clsx } from 'clsx'

import { useSplitEnabled } from '@buffer-mono/features'

import {
  SquareKanbanIcon,
  Button,
  Columns3Icon,
  LayoutDashboardIcon,
  IconButton,
  ListIcon,
  PageLayout,
  PlusIcon,
  TagIcon,
  ToggleGroup,
  WandSparklesIcon,
} from '@buffer-mono/popcorn'
import { UploadSource } from '@buffer-mono/uploader'

import { FilterButton } from '~publish/components/FilterButton'
import { FilterByTag } from '~publish/components/FilterByTag'
import {
  IdeaComposerPanelProvider,
  useIdeaComposer,
} from '~publish/components/IdeaManagementRouter'
import { ViewOnlyBadge } from '~publish/components/ViewOnlyBadge'
import { ViewOnlyPublishCoachMark } from '~publish/components/ViewOnlyCoachMarks'
import type { ViewOnlyCopy } from '~publish/components/ViewOnlyCoachMarks'
import {
  useHasOrganizationAction,
  useOrganizationId,
} from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import { sanitizeNullableArray } from '~publish/helpers/typeGuards'
import useIdeasUploader from '~publish/hooks/useIdeasUploader'
import { useIsMobileBreakpoint } from '~publish/hooks/useIsMobileBreakpoint'
import { usePageTitle } from '~publish/hooks/usePageTitle'
import { useQueryParam } from '~publish/hooks/useQueryParam'
import { GenerateIdeasButton } from '~publish/legacy/ai/components/GenerateIdeasButton'

import { CreatePrimaryAction, CreateTabsActions } from './CreateHeaderSlots'
import { Board } from './components/Board'
import {
  FilterByIdeaGroup,
  FilterByIdeaGroup_IdeaGroup,
} from './components/FilterByIdeaGroup/FilterByIdeaGroup'
import { IdeaBatchActions } from './components/IdeaBatchActions'
import { IdeasGallery } from './components/IdeasGallery'
import { IdeasList } from './components/IdeasList'
import {
  IdeasMultiSelectProvider,
  useIdeasMultiSelect,
} from './components/IdeasMultiSelect'

import styles from './IdeasPage.module.css'

import type { JSX } from 'react'

const VIEW_ONLY_IDEAS_COPY: ViewOnlyCopy = {
  title: 'You have view-only access to Ideas',
  body:
    'You can read the ideas your organization saved. To create, edit, or ' +
    'move ideas, ask an admin to update your permissions.',
}

const LOCAL_STORAGE_VIEW_KEY = 'contentPlannerLastViewed'

export const VIEWS = {
  GALLERY: 'gallery',
  BOARD: 'board',
  LIST: 'list',
}

export const GetCreateInfo = graphql(
  /* GraphQL */ `
    query GetCreateInfo($organizationId_ID: ID!) {
      ideaGroups(input: { organizationId: $organizationId_ID }) {
        id
        ...FilterByIdeaGroup_IdeaGroup
      }
    }
  `,
  [FilterByIdeaGroup_IdeaGroup],
)

type IdeasPageProps = {
  pageTitle?: string
}

function IdeasPage(props: IdeasPageProps): JSX.Element {
  const { pageTitle } = props
  usePageTitle(pageTitle ?? 'Create')
  const organizationId = useOrganizationId()

  const { createIdeaWithComposer } = useIdeaComposer()

  const isMobileScreenSize = useIsMobileBreakpoint()

  const { isEnabled: isListViewEnabled, isReady: isListViewReady } =
    useSplitEnabled('build-week-2026-ideas-list-view')

  const DEFAULT_VIEW = VIEWS.BOARD

  const [selectedTagIds = [], setSelectedTagIds] =
    useQueryParam<string[]>('tagIds')
  const [selectedGroupId = [], setSelectedGroupId] =
    useQueryParam<string[]>('groupIds')

  const { data } = useQuery(GetCreateInfo, {
    variables: {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      organizationId_ID: organizationId!,
    },
    skip: !organizationId,
  })

  // Whether `list` is a valid view depends on the flag.
  const validViews = React.useMemo(
    () =>
      isListViewEnabled ? Object.values(VIEWS) : [VIEWS.GALLERY, VIEWS.BOARD],
    [isListViewEnabled],
  )

  const [view, setView] = useQueryParam('view')
  React.useEffect(() => {
    if (!isListViewReady) return
    if (!view || !validViews.includes(view)) {
      // Validate the stored view too: it can hold `list` from a session
      // where the flag was on, which would loop this effect when off.
      const storedView = localStorage.getItem(LOCAL_STORAGE_VIEW_KEY)
      const initialView =
        storedView && validViews.includes(storedView)
          ? storedView
          : DEFAULT_VIEW
      setView(initialView, { replace: true })
    }
  }, [view, isListViewReady, validViews])

  // The URL can hold an invalid view (missing, junk, or `list` with the
  // flag off) until the effect above corrects it. Render and track from a
  // validated view so no frame renders nothing or reports a wrong source.
  const effectiveView = view && validViews.includes(view) ? view : DEFAULT_VIEW

  const isListView = effectiveView === VIEWS.LIST

  const uploader = useIdeasUploader()

  const handleUploadIfPossible = React.useCallback(
    (files: File[], uploadSource: UploadSource, groupId?: string) => {
      if (files.length > 0) {
        uploader.addFiles(files, {
          source: uploadSource,
        })
        const viewSource =
          effectiveView === VIEWS.LIST
            ? 'list'
            : effectiveView === VIEWS.GALLERY
              ? 'gallery'
              : 'board'
        createIdeaWithComposer({
          source: `create-${viewSource}-${uploadSource.trigger}-newIdea-1`,
          groupId,
        })
      }
    },
    [uploader, effectiveView],
  )

  const onDrop = React.useCallback(
    (files: File[], groupId?: string) => {
      handleUploadIfPossible(files, UploadSource.dragAndDrop(), groupId)
    },
    [handleUploadIfPossible],
  )

  const onPaste = React.useCallback(
    (files: File[], groupId?: string) => {
      handleUploadIfPossible(files, UploadSource.paste(), groupId)
    },
    [handleUploadIfPossible],
  )

  const handleViewSelect = (selectedValue: string): void => {
    if (!selectedValue) {
      return
    }
    setView(selectedValue)
    localStorage.setItem(LOCAL_STORAGE_VIEW_KEY, selectedValue)
  }

  const handleNewIdea = (): void => {
    createIdeaWithComposer({
      source:
        effectiveView === VIEWS.LIST
          ? 'create-list-toolbar-newIdea-1'
          : effectiveView === VIEWS.GALLERY
            ? 'create-gallery-toolbar-newIdea-1'
            : 'create-board-toolbar-newIdea-1',
    })
  }

  // Wait for the flag to resolve before rendering any view to avoid
  // flashing the wrong one while the feature flag SDK loads.
  if (!isListViewReady) {
    return <></>
  }

  return (
    <IdeasMultiSelectProvider>
      <IdeasHeaderControls
        view={effectiveView}
        showListOption={isListViewEnabled}
        groups={sanitizeNullableArray(data?.ideaGroups)}
        selectedTagIds={selectedTagIds}
        onSelectTags={setSelectedTagIds}
        selectedGroupId={selectedGroupId}
        onSelectGroups={setSelectedGroupId}
        isMobileScreenSize={isMobileScreenSize}
        onViewSelect={handleViewSelect}
        onNewIdea={handleNewIdea}
      />

      {isListView ? (
        <PageLayout.Container
          size="full"
          className={clsx(styles.main, styles.mainList)}
        >
          <IdeasList onDrop={onDrop} onPaste={onPaste} />
          {/* The composer routes render here: inline as a side panel on
              wide screens, portaled as a dialog otherwise. */}
          <IdeaComposerPanelProvider>
            <Outlet />
          </IdeaComposerPanelProvider>
        </PageLayout.Container>
      ) : (
        <>
          <PageLayout.Container
            size="full"
            className={clsx(
              styles.main,
              effectiveView === VIEWS.BOARD && styles.mainBoard,
            )}
          >
            {effectiveView === VIEWS.GALLERY && (
              <IdeasGallery onDrop={onDrop} onPaste={onPaste} />
            )}
            {effectiveView === VIEWS.BOARD && (
              <Board onDrop={onDrop} onPaste={onPaste} />
            )}
          </PageLayout.Container>
          <Outlet />
        </>
      )}
    </IdeasMultiSelectProvider>
  )
}

type IdeasHeaderControlsProps = {
  view: string | undefined
  showListOption?: boolean
  groups: React.ComponentProps<typeof FilterByIdeaGroup>['groups']
  selectedTagIds: string[]
  onSelectTags: (tagIds: string[]) => void
  selectedGroupId: string[]
  onSelectGroups: (groupIds: string[]) => void
  isMobileScreenSize: boolean
  onViewSelect: (view: string) => void
  onNewIdea: () => void
}

/**
 * Renders the Ideas controls into the shared Create header. While ideas are
 * being multi-selected, the tab controls are replaced by the batch actions and
 * the title-row actions are hidden. An actor without `manageIdeas` gets the
 * view-only badge in place of the idea creation actions.
 */
export const IdeasHeaderControls = ({
  view,
  showListOption = false,
  groups,
  selectedTagIds,
  onSelectTags,
  selectedGroupId,
  onSelectGroups,
  isMobileScreenSize,
  onViewSelect,
  onNewIdea,
}: IdeasHeaderControlsProps): JSX.Element => {
  const { selecting } = useIdeasMultiSelect()
  const canManageIdeas = useHasOrganizationAction('manageIdeas')

  return (
    <>
      <CreatePrimaryAction>
        {canManageIdeas ? (
          !selecting && (
            <>
              <GenerateIdeasButton>
                {isMobileScreenSize ? (
                  <IconButton
                    variant="tertiary"
                    size="medium"
                    id="generate-ideas-button"
                    label="Generate Ideas"
                  >
                    <WandSparklesIcon />
                  </IconButton>
                ) : undefined}
              </GenerateIdeasButton>
              <Button
                id="create-idea-button"
                variant={isMobileScreenSize ? 'primary' : 'secondary'}
                size="medium"
                onClick={onNewIdea}
                aria-label={isMobileScreenSize ? 'New Idea' : undefined}
                className={
                  isMobileScreenSize ? styles.mobileToolbarButton : undefined
                }
              >
                <PlusIcon />
                {!isMobileScreenSize && 'New Idea'}
              </Button>
            </>
          )
        ) : (
          <ViewOnlyPublishCoachMark copy={VIEW_ONLY_IDEAS_COPY}>
            <ViewOnlyBadge />
          </ViewOnlyPublishCoachMark>
        )}
      </CreatePrimaryAction>

      <CreateTabsActions>
        {selecting ? (
          <IdeaBatchActions />
        ) : (
          <>
            {(view === VIEWS.GALLERY || view === VIEWS.LIST) && (
              <FilterByIdeaGroup
                groups={groups}
                value={selectedGroupId}
                onSelect={onSelectGroups}
              >
                <FilterButton
                  icon={<Columns3Icon />}
                  count={selectedGroupId.length}
                  variant="tertiary"
                  size="small"
                  aria-label={
                    isMobileScreenSize ? 'Filter by groups' : undefined
                  }
                  hideChevron={isMobileScreenSize}
                >
                  {!isMobileScreenSize && 'Groups'}
                </FilterButton>
              </FilterByIdeaGroup>
            )}
            <FilterByTag value={selectedTagIds} onSelect={onSelectTags}>
              <FilterButton
                icon={<TagIcon />}
                count={selectedTagIds.length}
                variant="tertiary"
                size="small"
                aria-label={isMobileScreenSize ? 'Filter by tags' : undefined}
                hideChevron={isMobileScreenSize}
              >
                {!isMobileScreenSize && 'Tags'}
              </FilterButton>
            </FilterByTag>
            <ToggleGroup
              size="medium"
              value={view}
              onChange={onViewSelect}
              className={
                isMobileScreenSize ? styles.mobileLayoutToggle : undefined
              }
            >
              <ToggleGroup.Item
                value={VIEWS.BOARD}
                aria-label={isMobileScreenSize ? 'Board View' : undefined}
              >
                <SquareKanbanIcon />
                {!isMobileScreenSize && 'Board'}
              </ToggleGroup.Item>
              <ToggleGroup.Item
                value={VIEWS.GALLERY}
                aria-label={isMobileScreenSize ? 'Gallery View' : undefined}
              >
                <LayoutDashboardIcon />
                {!isMobileScreenSize && 'Gallery'}
              </ToggleGroup.Item>
              {showListOption && (
                <ToggleGroup.Item
                  value={VIEWS.LIST}
                  aria-label={isMobileScreenSize ? 'List View' : undefined}
                >
                  <ListIcon />
                  {!isMobileScreenSize && 'List'}
                </ToggleGroup.Item>
              )}
            </ToggleGroup>
          </>
        )}
      </CreateTabsActions>
    </>
  )
}

export { IdeasPage }
